import Item from '../../canvas-timeline/canvas_timeline_item';
import {
	AUTO_SCHEDULING_PERSON_WIDTH,
	COMMON_IMAGE,
	drawHexagon,
	drawRectangle,
	getTextColor,
	getTrimmedText,
	ITEM_TYPE,
	TIMELINE_BAR_BORDER_RADIUS,
	TIMELINE_GREY_TASK_BAR_COLOR,
	TIMELINE_BAR_PADDING_X,
	GROUP_SECTION_TEXT_GREY_DARK,
} from '../../canvas-timeline/canvas_timeline_util';
import Util from '../../../../forecast-app/shared/util/util';
import {profilePicSrc} from '../../../../constants';
import {cacheManager} from '../../canvas-timeline/canvas_timeline_cache_manager';
import Color from 'color';
import {hasFeatureFlag} from "../../../../forecast-app/shared/util/FeatureUtil";

let userPilotSelectedTaskItem = null;

const positionUserPilotTag = (taskId, x, y, width, height, userPilotDocumentId) => {
	if (userPilotSelectedTaskItem === null || userPilotSelectedTaskItem === taskId) {
		const foregroundCanvas = document.getElementById('foreground-canvas');
		const canvasBounds = foregroundCanvas.getBoundingClientRect();
		const upElement = document.getElementById(userPilotDocumentId);
		if (x >= 0 && y >= 0 && x + width < canvasBounds.width && y + height < canvasBounds.height) {
			upElement.style.left = `${x + canvasBounds.x}px`;
			upElement.style.top = `${y + canvasBounds.y}px`;
			upElement.style.height = `${height}px`;
			upElement.style.width = `${width}px`;
			userPilotSelectedTaskItem = taskId;
		} else {
			upElement.style.left = '0';
			upElement.style.top = '0';
			upElement.style.height = '0';
			upElement.style.width = '0';
			userPilotSelectedTaskItem = null;
		}
	}
};

class TaskItem extends Item {
	constructor(data) {
		super(ITEM_TYPE.PROJECT_SCHEDULING_TASK, data);
	}

	refreshData(updatedTask) {
		this.data.phaseId = updatedTask.phaseId;
	}

	draw(canvasContext, x, y, width, canvasWidth) {
		if (hasFeatureFlag("timeline_group_section_restyling")) {
			const {data, height} = this;
			const {color, faded, task, onPersonImageLoad} = data;
			const completion = task.progress < 0 ? 0 : task.progress;
			const textColor = GROUP_SECTION_TEXT_GREY_DARK;
			const modifiedProjectColor = color.toLowerCase() === '#e6e6ed' ? TIMELINE_GREY_TASK_BAR_COLOR : color;

			positionUserPilotTag(task.id, x, y, width, height, 'user-pilot-project-scheduling-task');

			let taskColor = Color(task.done ? '#6BD675' : '#E3E3ED');

			drawRectangle(canvasContext, x, y, width, height, {
				backgroundColor: taskColor.hex(),
				backgroundOpacity: faded ? 0.2 : 1,
				borderRadius: TIMELINE_BAR_BORDER_RADIUS,
			});

			if (completion !== 0 && (completion !== 100 || !task.done)) {
				//The last path drawn is the shape of item, use that as a clipping path to draw progress
				canvasContext.save();
				canvasContext.clip();

				let completionColor = Color(modifiedProjectColor);

				const completionRecOptions = {
					backgroundColor: completionColor.hex(),
					backgroundOpacity: faded ? 0.2 : 1,
				};
				const completionWidth = (width * completion) / 100;
				drawRectangle(canvasContext, x, y, completionWidth, height, completionRecOptions);
				canvasContext.restore();
			}

			if (task.autoSchedulingPerson) {
				const person = task.autoSchedulingPerson;
				const cachedImage = cacheManager.get('personImage', person.id);
				let img = null;

				if (cachedImage) {
					img = cachedImage;
				} else if (person.profilePictureId) {
					img = new Image();
					img.onload = () => {
						cacheManager.set('personImage', person.id, img);
						onPersonImageLoad();
					};
					img.src = person.profilePictureId ? profilePicSrc(person.profilePictureId) : null;
				} else {
					const initialsXCoor = x + width;
					drawHexagon(canvasContext, initialsXCoor, y, height, '#6e0fea', 1);

					canvasContext.font = '500 11px ' + Util.getFontFamily();
					canvasContext.fillStyle = 'white';
					canvasContext.textAlign = 'center';
					canvasContext.fillText(person.initials.toUpperCase(), initialsXCoor + 12, y + 18);
					canvasContext.textAlign = 'start';
				}
				img && canvasContext.drawImage(img, x + width, y, AUTO_SCHEDULING_PERSON_WIDTH, height);

				const warningIconData = {
					size: 20,
					offsetX: AUTO_SCHEDULING_PERSON_WIDTH - 4,
					offsetY: 3,
				};

				if (task.isOverAllocated) {
					drawHexagon(canvasContext, x + width, y + 1, height - 2, '', '', '#d0021b');
					canvasContext.drawImage(cacheManager.getCommonImage(COMMON_IMAGE.WARNING_ICON_RED), x + width + warningIconData.offsetX, y + warningIconData.offsetY, warningIconData.size, warningIconData.size);
				}

				if (task.showImageHover) {
					drawHexagon(canvasContext, x + width, y, height, '#000', 0.5);
					canvasContext.strokeStyle = '#fff';
					canvasContext.lineWidth = 2;
					canvasContext.globalAlpha = 1;

					canvasContext.beginPath();
					canvasContext.moveTo(x + width + 16, y + 9);
					canvasContext.lineTo(x + width + 8, y + height - 9);
					canvasContext.closePath();
					canvasContext.stroke();

					canvasContext.beginPath();
					canvasContext.moveTo(x + width + 8, y + 9);
					canvasContext.lineTo(x + width + 16, y + height - 9);
					canvasContext.closePath();
					canvasContext.stroke();
				}
			}

			const taskNameFont = '400 11px ' + Util.getFontFamily();
			const completionFont = '600 11px ' + Util.getFontFamily();

			const checkmarkMargin = task.done ? TIMELINE_BAR_PADDING_X : 0;
			const checkmarkSize = 12;
			const textIconSpacing = 8;
			const textMargin = task.done ? checkmarkMargin + checkmarkSize + textIconSpacing : TIMELINE_BAR_PADDING_X;
			const iconWidth = 16;
			const iconMargin = 5;
			const middlePadding = 16;
			const minimumTrim = 45;


			//Task Name
			let taskName = '';
			if (task) {
				taskName = task.name;
			}
			canvasContext.font = taskNameFont;
			const taskNameStringWidth = canvasContext.measureText(taskName).width;

			//Completion
			const completionString = completion === 100 ? '' : `${completion || 0}%`;
			canvasContext.font = completionFont;
			const completionStringWidth = canvasContext.measureText(completionString).width;

			// Visible item stats
			const visibleRightX = x + width > canvasWidth ? canvasWidth : x + width;
			const visibleLeftX = x > 0 ? x : 0;
			const visibleItemWidth = visibleRightX - visibleLeftX;

			const textTrim = visibleItemWidth - textMargin * 2;
			const showTaskName = textTrim > minimumTrim;

			// task done green checkmark icon
			const shouldShowDoneIcon = visibleItemWidth - (checkmarkMargin * 2) > checkmarkSize;
			if (task.done && shouldShowDoneIcon) {
				const checkmarkOffsetX = showTaskName ? checkmarkMargin : (width / 2) - (checkmarkSize / 2);
				canvasContext.drawImage(
					cacheManager.getCommonImage(COMMON_IMAGE.TASK_DONE_GREEN_CHECKMARK),
					x + checkmarkOffsetX,
					y + (height / 2) - (checkmarkSize / 2),
					checkmarkSize,
					checkmarkSize,
				);
			}

			let iconWidthNeeded = 0;
			if (task.highPriority) {
				iconWidthNeeded += iconWidth;
			}
			if (task.bug) {
				iconWidthNeeded += iconWidth;
			}
			if (task.blocked) {
				iconWidthNeeded += iconWidth;
			}

			const rightSize = iconWidthNeeded + (completionStringWidth + textMargin * 2);
			const showRight = visibleItemWidth - (rightSize + middlePadding) > taskNameStringWidth;

			if (!showTaskName && !showRight) {
				return;
			}

			//Task Name
			if (showTaskName) {
				canvasContext.fillStyle = textColor;
				canvasContext.font = taskNameFont;
				canvasContext.fillText(
					getTrimmedText(canvasContext, taskName, textTrim),
					textMargin + visibleLeftX,
					y + 17
				);
			}

			if (showRight) {
				// Icons
				let iconXCoord = visibleRightX;
				if (task.highPriority) {
					canvasContext.drawImage(
						cacheManager.getCommonImage(COMMON_IMAGE.TASK_HIGH_PRIORITY_BLACK),
						iconXCoord - iconWidth - completionStringWidth - textMargin,
						y + 7
					);
					iconXCoord -= iconWidth + iconMargin;
				}
				if (task.bug) {
					canvasContext.drawImage(
						cacheManager.getCommonImage(COMMON_IMAGE.TASK_BUG_BLACK),
						iconXCoord - iconWidth - completionStringWidth - textMargin,
						y + 6
					);
					iconXCoord -= iconWidth + iconMargin;
				}
				if (task.blocked) {
					canvasContext.drawImage(
						cacheManager.getCommonImage(COMMON_IMAGE.TASK_BLOCKED_BLACK),
						iconXCoord - iconWidth - completionStringWidth - textMargin,
						y + 6
					);
				}

				// Completion
				if (completion < 100) {
					canvasContext.fillStyle = textColor;
					canvasContext.font = completionFont;
					canvasContext.fillText(
						completionString,
						visibleRightX - textMargin - completionStringWidth,
						y + 17
					);
				}
			}
		} else {
			const {data, height} = this;
			const {color, faded, task, onPersonImageLoad} = data;
			const completion = task.progress < 0 ? 0 : task.progress;
			const textColor = getTextColor(color);

			positionUserPilotTag(task.id, x, y, width, height, 'user-pilot-project-scheduling-task');

			let taskColor = Color(task.done ? '#00CC66' : '#cccccc');

			drawRectangle(canvasContext, x, y, width, height, {
				backgroundColor: taskColor.hex(),
				isCornerTopRightCutOff: true,
				backgroundOpacity: faded ? 0.2 : 1,
			});

			if (completion !== 0 && (completion !== 100 || !task.done)) {
				//The last path drawn is the shape of item, use that as a clipping path to draw progress
				canvasContext.save();
				canvasContext.clip();

				let completionColor = Color(color);

				const completionRecOptions = {
					backgroundColor: completionColor.hex(),
					backgroundOpacity: faded ? 0.2 : 1,
				};
				const completionWidth = (width * completion) / 100;
				drawRectangle(canvasContext, x, y, completionWidth, height, completionRecOptions);
				canvasContext.restore();
			}

			if (task.autoSchedulingPerson) {
				const person = task.autoSchedulingPerson;
				const cachedImage = cacheManager.get('personImage', person.id);
				let img = null;

				if (cachedImage) {
					img = cachedImage;
				} else if (person.profilePictureId) {
					img = new Image();
					img.onload = () => {
						cacheManager.set('personImage', person.id, img);
						onPersonImageLoad();
					};
					img.src = person.profilePictureId ? profilePicSrc(person.profilePictureId) : null;
				} else {
					const initialsXCoor = x + width;
					drawHexagon(canvasContext, initialsXCoor, y, height, '#6e0fea', 1);

					canvasContext.font = '500 11px ' + Util.getFontFamily();
					canvasContext.fillStyle = 'white';
					canvasContext.textAlign = 'center';
					canvasContext.fillText(person.initials.toUpperCase(), initialsXCoor + 12, y + 18);
					canvasContext.textAlign = 'start';
				}
				img && canvasContext.drawImage(img, x + width, y, AUTO_SCHEDULING_PERSON_WIDTH, height);

				const warningIconData = {
					size: 20,
					offsetX: AUTO_SCHEDULING_PERSON_WIDTH - 4,
					offsetY: 3,
				};

				if (task.isOverAllocated) {
					drawHexagon(canvasContext, x + width, y + 1, height - 2, '', '', '#d0021b');
					canvasContext.drawImage(cacheManager.getCommonImage(COMMON_IMAGE.WARNING_ICON_RED), x + width + warningIconData.offsetX, y + warningIconData.offsetY, warningIconData.size, warningIconData.size);
				}

				if (task.showImageHover) {
					drawHexagon(canvasContext, x + width, y, height, '#000', 0.5);
					canvasContext.strokeStyle = '#fff';
					canvasContext.lineWidth = 2;
					canvasContext.globalAlpha = 1;

					canvasContext.beginPath();
					canvasContext.moveTo(x + width + 16, y + 9);
					canvasContext.lineTo(x + width + 8, y + height - 9);
					canvasContext.closePath();
					canvasContext.stroke();

					canvasContext.beginPath();
					canvasContext.moveTo(x + width + 8, y + 9);
					canvasContext.lineTo(x + width + 16, y + height - 9);
					canvasContext.closePath();
					canvasContext.stroke();
				}
			}

			const textMargin = 16;
			const iconWidth = 16;
			const iconMargin = 5;
			const middlePadding = 16;
			const minimumTrim = 45;

			let taskName = '';
			if (task) {
				taskName = task.name;
			}
			//Task Name
			canvasContext.font = '12px ' + Util.getFontFamily();
			const taskNameStringWidth = canvasContext.measureText(taskName).width;

			//Completion
			const completionString = `${completion || 0}%`;
			canvasContext.font = '500 12px ' + Util.getFontFamily();
			const completionStringWidth = canvasContext.measureText(completionString).width;

			// Visible item stats
			const visibleRightX = x + width > canvasWidth ? canvasWidth : x + width;
			const visibleLeftX = x > 0 ? x : 0;
			const visibleItemWidth = visibleRightX - visibleLeftX;

			const textTrim = visibleItemWidth - textMargin * 2;

			let iconWidthNeeded = 0;
			if (task.highPriority) {
				iconWidthNeeded += iconWidth;
			}
			if (task.bug) {
				iconWidthNeeded += iconWidth;
			}
			if (task.blocked) {
				iconWidthNeeded += iconWidth;
			}

			const rightSize = iconWidthNeeded + (completionStringWidth + textMargin * 2);

			const showLeft = textTrim > minimumTrim;
			const showRight = visibleItemWidth - (rightSize + middlePadding) > taskNameStringWidth;

			if (!showLeft && !showRight) {
				return;
			}

			//Task Name
			if (showLeft) {
				canvasContext.fillStyle = textColor;
				canvasContext.font = '12px ' + Util.getFontFamily();
				canvasContext.fillText(getTrimmedText(canvasContext, taskName, textTrim), textMargin + visibleLeftX, y + 18);
			}

			if (showRight) {
				// Icons
				let iconXCoord = visibleRightX;
				if (task.highPriority) {
					canvasContext.drawImage(cacheManager.getCommonImage(COMMON_IMAGE.HIGH_PRIORITY_FLAG_WHITE), iconXCoord - iconWidth - completionStringWidth - textMargin, y + 7);
					iconXCoord -= iconWidth + iconMargin;
				}
				if (task.bug) {
					canvasContext.drawImage(cacheManager.getCommonImage(COMMON_IMAGE.BUG), iconXCoord - iconWidth - completionStringWidth - textMargin, y + 6);
					iconXCoord -= iconWidth + iconMargin;
				}
				if (task.blocked) {
					canvasContext.drawImage(cacheManager.getCommonImage(COMMON_IMAGE.BLOCKED), iconXCoord - iconWidth - completionStringWidth - textMargin, y + 6);
				}

				// Completion
				canvasContext.fillStyle = textColor;
				canvasContext.font = '500 12px ' + Util.getFontFamily();
				canvasContext.fillText(completionString, visibleRightX - textMargin - completionStringWidth, y + 18);
			}
		}
	}
}
export default TaskItem;
